// Who is signed in, held in one place.
//
// The app asks the server on first load rather than trusting anything in local
// storage. The session cookie is httpOnly, so the browser cannot read it — the
// only honest answer to "am I signed in?" comes from the server, and that also
// means a session revoked by an admin is noticed on the next reload.
//
// WITH ONE EXCEPTION, for offline form fill (round 42): when the server
// cannot be REACHED at all, the app opens on the last signed-in session this
// phone saw, marked `offline`. That is not trusting the phone about access:
// every request still rides the cookie and the server still decides. It is
// what lets somebody in a dead spot reach the Forms page, whose saved copy is
// the only thing that works there. A server answer always wins: "signed out"
// clears the saved session, and a reconnect re-asks.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError, type SessionState, type Tenant, type User } from './api';
import { offlineStore } from './offline/store';

interface SessionContextValue {
  loading: boolean;
  user: User | null;
  tenant: Tenant | null;
  /** Opened on the phone's saved session because the server was unreachable. */
  offline: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** "Could not reach Dayboard", as opposed to an answer from it. */
function unreachable(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504)
  );
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState | null>(null);
  const [offline, setOffline] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const next = await api.session();
      setState(next);
      setOffline(false);
      // Kept (or cleared) best-effort: a phone that cannot store simply has
      // no offline start, and nothing else changes.
      if (next.signedIn) {
        void offlineStore()
          .putSession({ user: next.user, tenant: next.tenant, savedAt: new Date().toISOString() })
          .catch(() => undefined);
      } else {
        void offlineStore().clearSession().catch(() => undefined);
      }
    } catch (error) {
      if (unreachable(error)) {
        const saved = await offlineStore()
          .getSession()
          .catch(() => undefined);
        if (saved) {
          setState({ signedIn: true, user: saved.user, tenant: saved.tenant });
          setOffline(true);
          return;
        }
      }
      setState({ signedIn: false });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Opened offline: ask again the moment the phone says it is back, so the
  // server's answer replaces the saved one as soon as there is one to have.
  useEffect(() => {
    if (!offline) return;
    const onOnline = () => void refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [offline, refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Signing out takes the saved session AND the company's saved lists off
      // the phone, which may be shared. Forms not yet sent are deliberately
      // KEPT (the shell warns first): deleting somebody's finished prestart
      // because they signed out is not a trade anyone would make.
      const signedIn = state?.signedIn ? state : null;
      void offlineStore().clearSession().catch(() => undefined);
      if (signedIn) {
        void offlineStore()
          .deletePack(`${signedIn.tenant.id}:${signedIn.user.id}`)
          .catch(() => undefined);
      }
      setOffline(false);
      setState({ signedIn: false });
    }
  }, [state]);

  const value = useMemo<SessionContextValue>(
    () => ({
      loading: state === null,
      user: state?.signedIn ? state.user : null,
      tenant: state?.signedIn ? state.tenant : null,
      offline,
      refresh,
      signOut,
    }),
    [state, offline, refresh, signOut]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * The session when there is one, and null when the component is rendered
 * outside a provider. For a page that only ADDS something when a company
 * setting is on (the client page's Quotes tab, round 43): it must still
 * render where no session is mounted, which is how its existing tests render
 * it, and a hidden tab is the right answer when the setting is unknown.
 */
export function useOptionalSession(): SessionContextValue | null {
  return useContext(SessionContext);
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}
